import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { useCreateRepairTicketMutation } from '../../../store/api/repairApi';
import { useLazyGetCustomersQuery } from '../../../store/api/salesApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { CustomerFormContent } from '../../sales/pages/CustomerForm';

export default function RepairTicketForm() {
  const navigate = useNavigate();
  const [createTicket, { isLoading, error }] = useCreateRepairTicketMutation();
  const [triggerCustomerSearch, { data: customerSearchData, isFetching: searchingCustomers }] = useLazyGetCustomersQuery();
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';

  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);

  const [formData, setFormData] = useState({
    customerId: '',
    deviceModel: '',
    deviceImei: '',
    faultDescription: '',
    deviceCondition: '',
    estimatedCost: '',
  });

  useEffect(() => {
    if (customerSearchData) {
      const data = customerSearchData.data || (Array.isArray(customerSearchData) ? customerSearchData : []);
      setCustomerSearchOptions(prevOptions => {
        const currentId = formData.customerId;
        const currentInNew = data.find(c => c.id === currentId);
        const currentInOld = prevOptions.find(c => c.id === currentId);
        if (currentId && !currentInNew && currentInOld) {
          return [currentInOld, ...data];
        }
        return data;
      });
    }
  }, [customerSearchData, formData.customerId]);

  const selectedCustomer = customerSearchOptions.find((c) => c.id === formData.customerId) || null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = await createTicket(formData).unwrap();
      navigate(`/repairs/${result.id}`);
    } catch (err) {
      console.error('Failed to create repair ticket:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Box maxWidth="800px" mx="auto">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1">
          New Repair Ticket
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/repairs')}>
          Cancel
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error.data?.message || 'Failed to create ticket'}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6">Customer Details</Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Autocomplete
                  options={customerSearchOptions}
                  loading={searchingCustomers}
                  getOptionLabel={(option) => `${option.name} (${option.phone})`}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  value={selectedCustomer}
                  filterOptions={(x) => x}
                  onChange={(event, newValue) => {
                    setFormData((prev) => ({ ...prev, customerId: newValue ? newValue.id : '' }));
                  }}
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === 'input') {
                      if (newInputValue && newInputValue.length >= 2) {
                        triggerCustomerSearch({ search: newInputValue, limit: 20 });
                      } else if (!newInputValue) {
                        triggerCustomerSearch({ limit: 20 });
                      }
                    }
                  }}
                  onOpen={() => {
                    if (customerSearchOptions.length === 0) triggerCustomerSearch({ limit: 20 });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Customer"
                      required
                      fullWidth
                    />
                  )}
                  sx={{ flexGrow: 1 }}
                />
                <Button
                  variant="outlined"
                  onClick={() => setCustomerDialogOpen(true)}
                  sx={{ minWidth: 'fit-content' }}
                >
                  + Add Customer
                </Button>
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" sx={{ mt: 2 }}>Device Details</Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                required
                fullWidth
                label="Device Model"
                name="deviceModel"
                value={formData.deviceModel}
                onChange={handleChange}
                placeholder="e.g. iPhone 13 Pro"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="IMEI / Serial Number"
                name="deviceImei"
                value={formData.deviceImei}
                onChange={handleChange}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                required
                fullWidth
                multiline
                rows={3}
                label="Fault Description"
                name="faultDescription"
                value={formData.faultDescription}
                onChange={handleChange}
                placeholder="Describe the issue reported by the customer..."
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Device Condition (Physical)"
                name="deviceCondition"
                value={formData.deviceCondition}
                onChange={handleChange}
                placeholder="e.g. Scratched screen, dent on top right corner..."
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label={`Estimated Cost (${currency})`}
                name="estimatedCost"
                value={formData.estimatedCost}
                onChange={handleChange}
                InputProps={{ inputProps: { min: 0, step: '0.01' } }}
              />
            </Grid>

            <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={isLoading || !formData.customerId}
              >
                {isLoading ? 'Creating...' : 'Create Ticket'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Add Customer Dialog */}
      <Dialog
        open={customerDialogOpen}
        onClose={() => setCustomerDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1.5, boxShadow: '0 12px 40px rgba(0,0,0,0.15)' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Add Customer</DialogTitle>
        <DialogContent
          sx={{
            pt: 2,
            overflow: 'visible',
            '& .MuiInputLabel-root': { overflow: 'visible' },
          }}
        >
          <CustomerFormContent
            compact
            onCancel={() => setCustomerDialogOpen(false)}
            onSuccess={(created) => {
              setCustomerDialogOpen(false);
              refetchCustomers();
              if (created?.id) {
                setSelectedCustomer(created);
                setFormData((prev) => ({ ...prev, customerId: created.id }));
              }
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
